import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import type CrmCoreUser from '@/types/serializers/CrmCoreUser'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { ReassignmentPreview } from './ReassignmentPreview'

interface RemoveUserDialogProps {
  user: CrmCoreUser | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PreviewResult {
  success: boolean
  summary?: Record<string, number>
  target_user?: string
  errors?: string[]
}

export function RemoveUserDialog({
  user,
  open,
  onOpenChange,
}: RemoveUserDialogProps) {
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Fetch preview on open
  useEffect(() => {
    if (open && user) {
      setLoading(true)
      setPreview(null)

      fetch(`/admin/users/${user.id}/remove_preview`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then((res) => res.json())
        .then((data: PreviewResult) => {
          setPreview(data)
          setLoading(false)
        })
        .catch(() => {
          setPreview({ success: false, errors: ['Failed to load preview'] })
          setLoading(false)
        })
    } else if (!open) {
      setPreview(null)
      setLoading(false)
      setProcessing(false)
    }
  }, [open, user])

  const handleConfirm = () => {
    if (!user) return
    setProcessing(true)
    router.delete(`/admin/users/${user.id}/remove`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setProcessing(false),
    })
  }

  const isBlocked = loading || (preview !== null && !preview.success) || processing

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {user?.name}</AlertDialogTitle>
          <AlertDialogDescription>
            This will lock the user&apos;s account and archive their profile. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ReassignmentPreview
          summary={preview?.summary ?? {}}
          targetUser={preview?.target_user ?? ''}
          loading={loading}
          error={preview && !preview.success ? preview.errors?.join(', ') : undefined}
        />

        <Separator />

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleConfirm}
            disabled={isBlocked}
          >
            {processing ? 'Removing...' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
